"use client";

import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useGetFolders } from "../../../hooks/useGetFolders";
import { useStorageQuery } from "../../../hooks/storageQueryProvider";
import { usePersistedView } from "../../../hooks/usePersistedView";
import SortBy from "./header/sortBy";
import OrderBy from "./header/orderBy";
import FoldersPagination from "./footer/pagination";
import SelectAll from "./header/selectAll";
import FolderCard from "./card/folderCard.";
import View from "./header/view";
import FolderCardSkeleton from "./card/folderCardSkeleton";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

export default function FoldersGrid({
    setIsNoFolders,
}: {
    setIsNoFolders: Dispatch<SetStateAction<boolean>>;
}) {
    const { foldersQuery, parentFolder } = useStorageQuery();

    const { data, isPending } = useGetFolders({
        ...foldersQuery,
        folderId: parentFolder?.id,
    });

    const folders = useMemo(() => data?.data, [data?.data]);
    const totalFolders = useMemo(() => data?.total, [data?.total]);

    useEffect(() => {
        setIsNoFolders(!isPending && totalFolders === 0);
    }, [totalFolders, isPending, setIsNoFolders]);

    const [view, setView] = usePersistedView("folders");

    const numOfSkeletons = Math.min(
        view === "grid" ? 12 : 8,
        foldersQuery.resultsPerPage
    );

    if (!isPending && !folders?.length) {
        return null;
    }

    return (
        <div>
            <div className="flex flex-col gap-2 md:gap-0 md:flex-row justify-between md:items-center">
                <div className="flex flex-row gap-4">
                    <h3 className=" text-xl font-semibold text-muted-foreground">
                        {t("storage.folders-grid.title")}
                    </h3>
                </div>
                {!isPending && (
                    <div className="flex flex-col w-fit md:flex-row gap-2">
                        <SortBy />
                        <OrderBy />
                        <SelectAll folders={folders} />
                        <View view={view} setView={setView} />
                    </div>
                )}
            </div>
            <div
                className={cn(
                    "mt-4",
                    view === "grid" &&
                        "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4",
                    view === "list" &&
                        "flex w-full flex-col divide-y-2 rounded-md border"
                )}
            >
                {isPending
                    ? Array.from({ length: numOfSkeletons }, (_, i) => i).map(
                          (_, idx) => (
                              <FolderCardSkeleton key={idx} view={view} />
                          )
                      )
                    : folders?.map((folder) => {
                          return (
                              <FolderCard
                                  key={folder.id}
                                  folder={folder}
                                  view={view}
                              />
                          );
                      })}
            </div>

            {!isPending && (
                <FoldersPagination totalNumberOfFolders={totalFolders || 0} />
            )}
        </div>
    );
}
